/**
 * Location Store Helpers
 *
 * Shared utility functions for location filtering and persistence.
 */

/**
 * LocalStorage key for persisting search point
 */
export const SEARCH_POINT_STORAGE_KEY = 'peach-truck-map-search-point';

/**
 * Search result coordinates for proximity filtering
 */
export interface SearchPoint {
  lat: number;
  lng: number;
  radiusMiles?: number; // Optional radius for filtering (default: 50 miles)
}

/**
 * Map bounds for viewport filtering
 */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Convert degrees to radians
 */
export function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 *
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in miles
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Load search point from localStorage
 *
 * @returns SearchPoint object or null if not found or invalid
 */
export function loadSearchPointFromStorage(): SearchPoint | null {
  try {
    // Guard against missing localStorage (e.g., in test environment before mocks are set up)
    if (typeof localStorage === 'undefined') return null;

    const stored = localStorage.getItem(SEARCH_POINT_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as SearchPoint;

    // Validate structure
    if (
      typeof parsed.lat !== 'number' ||
      typeof parsed.lng !== 'number' ||
      isNaN(parsed.lat) ||
      isNaN(parsed.lng)
    ) {
      console.warn('[LocationStore] Invalid search point in localStorage, clearing');
      localStorage.removeItem(SEARCH_POINT_STORAGE_KEY);
      return null;
    }

    // Validate optional radiusMiles
    if (parsed.radiusMiles !== undefined) {
      if (typeof parsed.radiusMiles !== 'number' || isNaN(parsed.radiusMiles)) {
        console.warn('[LocationStore] Invalid radiusMiles in localStorage, using default');
        parsed.radiusMiles = 50;
      }
    }

    return parsed;
  } catch (error) {
    console.error('[LocationStore] Error loading search point from localStorage:', error);
    return null;
  }
}

/**
 * Save search point to localStorage
 *
 * @param point - SearchPoint to save (null to clear)
 */
export function saveSearchPointToStorage(point: SearchPoint | null): void {
  try {
    // Guard against missing localStorage
    if (typeof localStorage === 'undefined') return;

    if (point === null) {
      localStorage.removeItem(SEARCH_POINT_STORAGE_KEY);
    } else {
      localStorage.setItem(SEARCH_POINT_STORAGE_KEY, JSON.stringify(point));
    }
  } catch (error) {
    // Handle quota exceeded or privacy mode errors gracefully
    console.warn('[LocationStore] Error saving search point to localStorage:', error);
  }
}
